import { useCallback, useContext, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { AppContext } from "../../context/AppContext.js";
import useHttp from "../../hooks/useHttp.jsx";
import { GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY } from "../constants/globalSearchConstants.js";

const GLOBAL_SEARCH_DEBOUNCE_MS_TO_FETCH_DATA = 400;
const MIN_LENGTH_OF_GLOBAL_SEARCH_QUERY = 2;
const MAXIMUM_PAGE_SIZE_OF_THE_GLOBAL_SEARCH_DATA = 10;

export default function useGlobalSearch() {
    const [appProperties] = useContext(AppContext);
    const { fetchDataByPagination } = useHttp();
    const [globalSearchResults, setGlobalSearchResults] = useState([]);
    const [isGlobalSearchDataWasFetching, setIsGlobalSearchDataWasFetching] = useState(false);
    const [isGlobalSearchFetchingDataByPagination, setIsGlobalSearchFetchingDataByPagination] = useState(false);
    const [isMoreDataPresentInPagination, setIsMoreDataPresentInPagination] = useState(false);
    const lastGlobalSearchQueryRef = useRef("");
    const lastActiveCategory = useRef(GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY);
    const abortControllerRef = useRef(null);

    const fetchPage = useCallback(
        async (globalSearchQuery, activeCategory, paginationStartIndex, signal) => {
            const activeCategoryParam = activeCategory === GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY ? "" : `&type=${encodeURIComponent(activeCategory)}`;
            const response = await fetchDataByPagination(
                `search?text=${encodeURIComponent(globalSearchQuery?.trim())}${activeCategoryParam}`,
                "GET",
                null,
                appProperties,
                null,
                MAXIMUM_PAGE_SIZE_OF_THE_GLOBAL_SEARCH_DATA,
                { start: paginationStartIndex },
                null,
                signal
            );
            const data = response?.data ?? response;
            return Array.isArray(data) ? data : [];
        },
        [fetchDataByPagination, appProperties]
    );

    const debounceFetchData = useDebouncedCallback(
        async (globalSearchQuery, activeCategory) => {
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            lastGlobalSearchQueryRef.current = globalSearchQuery.trim();
            lastActiveCategory.current = activeCategory || GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY;
            setIsGlobalSearchDataWasFetching(true);
            try {
                const globalSearchResponse = await fetchPage(globalSearchQuery, activeCategory, 0, abortController?.signal);
                if (abortController?.signal?.aborted) return;
                setGlobalSearchResults(globalSearchResponse);
                setIsMoreDataPresentInPagination(globalSearchResponse?.length >= MAXIMUM_PAGE_SIZE_OF_THE_GLOBAL_SEARCH_DATA);
            } catch (e) {
                if (abortController?.signal?.aborted) return;
                setGlobalSearchResults([]);
                setIsMoreDataPresentInPagination(false);
            } finally {
                if (!abortController?.signal?.aborted) {
                    setIsGlobalSearchDataWasFetching(false);
                }
            }
        },
        GLOBAL_SEARCH_DEBOUNCE_MS_TO_FETCH_DATA
    );

    const doGlobalSearch = useCallback(
        (globalSearchQuery, activeCategory = GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY) => {
            if (!globalSearchQuery || typeof globalSearchQuery !== "string" || globalSearchQuery?.trim()?.length < MIN_LENGTH_OF_GLOBAL_SEARCH_QUERY) {
                debounceFetchData?.cancel();
                setGlobalSearchResults([]);
                setIsMoreDataPresentInPagination(false);
                return;
            }
            debounceFetchData(globalSearchQuery, activeCategory);
        },
        [debounceFetchData]
    );

    const loadMoreGlobalSearchResults = useCallback(async () => {
        const globalSearchpaginationQuery = lastGlobalSearchQueryRef.current;
        const activeCategory = lastActiveCategory.current;
        if (!globalSearchpaginationQuery || isGlobalSearchDataWasFetching || isGlobalSearchFetchingDataByPagination || !isMoreDataPresentInPagination) return;

        const controller = new AbortController();
        setIsGlobalSearchFetchingDataByPagination(true);
        try {
            const data = await fetchPage(globalSearchpaginationQuery, activeCategory, globalSearchResults?.length, controller?.signal);
            if (controller.signal.aborted) return;
            setGlobalSearchResults((prev) => [...prev, ...data]);
            setIsMoreDataPresentInPagination(data?.length >= MAXIMUM_PAGE_SIZE_OF_THE_GLOBAL_SEARCH_DATA);
        } catch (e) {
            if (controller.signal.aborted) return;
            setIsMoreDataPresentInPagination(false);
        } finally {
            if (!controller.signal.aborted) {
                setIsGlobalSearchFetchingDataByPagination(false);
            }
        }
    }, [fetchPage, globalSearchResults.length, isGlobalSearchDataWasFetching, isGlobalSearchFetchingDataByPagination, isMoreDataPresentInPagination]);

    const clearGlobalSearchResults = useCallback((isFilterChange = false) => {
        setGlobalSearchResults([]);
        setIsMoreDataPresentInPagination(false);
        if (!isFilterChange) {
            lastGlobalSearchQueryRef.current = "";
        }
        debounceFetchData.cancel();
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, [debounceFetchData]);

    return { doGlobalSearch, globalSearchResults, isGlobalSearchDataWasFetching, setIsGlobalSearchDataWasFetching, isGlobalSearchFetchingDataByPagination, isMoreDataPresentInPagination, loadMoreGlobalSearchResults, clearGlobalSearchResults };
}
